import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { invoiceDraftsAPI, invoicesAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Textarea } from './ui/textarea';
import { FileText, Plus, Loader2, Check, Edit2, X, DollarSign, Clock, CheckCircle, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

/**
 * JobInvoiceSection — Stage 7
 * "Create Invoice from Materials" section on JobDetail page.
 */
export function JobInvoiceSection({ jobId }) {
  const { isOwner, isOffice } = useAuth();
  const canEdit = isOwner || isOffice;

  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [previewLines, setPreviewLines] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  const fetchDrafts = useCallback(async () => {
    try {
      const res = await invoiceDraftsAPI.listDrafts(jobId);
      setDrafts(res.data || []);
    } catch { /* non-critical */ }
    setLoading(false);
  }, [jobId]);

  useEffect(() => { fetchDrafts(); }, [fetchDrafts]);

  const handlePreview = async () => {
    setLoadingPreview(true);
    try {
      const res = await invoiceDraftsAPI.suggestedLines(jobId);
      const lines = res.data || [];
      if (lines.length === 0) {
        toast.error('No materials to invoice — add materials first');
        setPreviewLines(null);
      } else {
        setPreviewLines(lines);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load preview');
    }
    setLoadingPreview(false);
  };

  const handleCreateDraft = async () => {
    setCreating(true);
    try {
      await invoiceDraftsAPI.createDraft(jobId);
      toast.success('Invoice draft created');
      setPreviewLines(null);
      fetchDrafts();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to create draft');
    }
    setCreating(false);
  };

  if (loading) return null;

  return (
    <div className="space-y-4" data-testid="job-invoice-section">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold tracking-tight flex items-center gap-2">
          <FileText className="w-4 h-4" /> Create Invoice from Materials
        </h3>
        {canEdit && !previewLines && (
          <Button variant="outline" size="sm" onClick={handlePreview} disabled={loadingPreview} data-testid="preview-invoice-btn">
            {loadingPreview ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <DollarSign className="w-4 h-4 mr-1" />}
            Preview Invoice
          </Button>
        )}
      </div>

      {/* Preview */}
      {previewLines && (
        <Card className="border-primary/30" data-testid="invoice-preview">
          <CardHeader className="pb-2 pt-3">
            <CardTitle className="text-sm">Invoice Preview</CardTitle>
          </CardHeader>
          <CardContent>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-xs text-muted-foreground">
                  <th className="pb-2 pr-4">Item</th>
                  <th className="pb-2 pr-4 text-right">Qty</th>
                  <th className="pb-2 pr-4 text-right">Unit Price</th>
                  <th className="pb-2 text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {previewLines.map((line, idx) => (
                  <tr key={idx} className="border-b last:border-0" data-testid={`preview-line-${idx}`}>
                    <td className="py-2 pr-4">{line.item_name} {line.item_sku ? `(${line.item_sku})` : ''}</td>
                    <td className="py-2 pr-4 text-right tabular-nums">{line.qty}</td>
                    <td className="py-2 pr-4 text-right tabular-nums">${line.unit_price.toFixed(2)}</td>
                    <td className="py-2 text-right tabular-nums font-medium">${line.line_total.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="font-semibold">
                  <td colSpan={3} className="pt-2 text-right">Subtotal:</td>
                  <td className="pt-2 text-right tabular-nums" data-testid="preview-subtotal">
                    ${previewLines.reduce((s, l) => s + l.line_total, 0).toFixed(2)}
                  </td>
                </tr>
              </tfoot>
            </table>
            <div className="flex justify-end gap-2 mt-3">
              <Button variant="outline" size="sm" onClick={() => setPreviewLines(null)}>
                <X className="w-4 h-4 mr-1" /> Cancel
              </Button>
              <Button size="sm" onClick={handleCreateDraft} disabled={creating} data-testid="create-draft-btn">
                {creating ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
                Generate Invoice Draft
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Existing Drafts */}
      {drafts.length > 0 && (
        <div className="space-y-3">
          {drafts.map((draft) => (
            <DraftCard key={draft.id} draft={draft} canEdit={canEdit} onUpdated={fetchDrafts} jobId={jobId} />
          ))}
        </div>
      )}
    </div>
  );
}

function DraftCard({ draft, canEdit, onUpdated, jobId }) {
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [lines, setLines] = useState(draft.lines || []);
  const [notes, setNotes] = useState(draft.notes || '');
  const [saving, setSaving] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [convertingToInvoice, setConvertingToInvoice] = useState(false);

  const isDraft = draft.status === 'draft';
  const statusIcon = draft.status === 'finalized' ? CheckCircle : Clock;
  const StatusIcon = statusIcon;
  const statusColor = draft.status === 'finalized' ? 'text-green-600' : draft.status === 'voided' ? 'text-red-600' : 'text-amber-600';

  const handleLineChange = (lineId, field, value) => {
    setLines(prev => prev.map(l => {
      if (l.id !== lineId) return l;
      const updated = { ...l, [field]: field === 'qty' ? parseInt(value, 10) || 0 : parseFloat(value) || 0 };
      updated.line_total = updated.qty * updated.unit_price;
      return updated;
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await invoiceDraftsAPI.updateDraft(draft.id, {
        lines: lines.map(l => ({
          id: l.id,
          qty: l.qty,
          unit_price: l.unit_price,
          description: l.description,
        })),
        notes,
      });
      toast.success('Draft updated');
      setEditing(false);
      onUpdated();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to update');
    }
    setSaving(false);
  };

  const handleFinalize = async () => {
    setFinalizing(true);
    try {
      await invoiceDraftsAPI.finalizeDraft(draft.id);
      toast.success('Invoice finalized');
      onUpdated();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to finalize');
    }
    setFinalizing(false);
  };

  const handleOpenInEditor = async () => {
    setConvertingToInvoice(true);
    try {
      const res = await invoicesAPI.fromJob(jobId);
      toast.success('Invoice created — opening editor');
      navigate(`/invoices/${res.data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to create invoice');
    }
    setConvertingToInvoice(false);
  };

  const subtotal = lines.reduce((s, l) => s + (l.qty * l.unit_price), 0);
  const createdDate = draft.created_at ? new Date(draft.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';

  return (
    <Card className={draft.status === 'voided' ? 'opacity-50' : ''} data-testid={`draft-card-${draft.id}`}>
      <CardHeader className="pb-2 pt-3 px-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <StatusIcon className={`w-4 h-4 ${statusColor}`} />
            <span className="text-sm font-semibold">Invoice Draft</span>
            <span className={`text-xs px-1.5 py-0.5 rounded capitalize ${
              draft.status === 'finalized' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
              draft.status === 'voided' ? 'bg-red-100 text-red-700' :
              'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
            }`} data-testid={`draft-status-${draft.id}`}>{draft.status}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">{createdDate}</span>
            {canEdit && isDraft && !editing && (
              <Button variant="ghost" size="sm" onClick={() => setEditing(true)} data-testid={`edit-draft-${draft.id}`}>
                <Edit2 className="w-3.5 h-3.5" />
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="px-4 pb-3">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b text-left text-xs text-muted-foreground">
              <th className="pb-2 pr-4">Item</th>
              <th className="pb-2 pr-4 text-right w-20">Qty</th>
              <th className="pb-2 pr-4 text-right w-28">Unit Price</th>
              <th className="pb-2 text-right w-24">Total</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((line) => (
              <tr key={line.id} className="border-b last:border-0" data-testid={`draft-line-${line.id}`}>
                <td className="py-2 pr-4">{line.description || line.item_name || 'Item'} {line.item_sku ? `(${line.item_sku})` : ''}</td>
                <td className="py-2 pr-4 text-right">
                  {editing ? (
                    <Input type="number" min="1" step="1" value={line.qty} onChange={(e) => handleLineChange(line.id, 'qty', e.target.value)} className="w-16 h-7 text-right text-sm" data-testid={`edit-qty-${line.id}`} />
                  ) : (
                    <span className="tabular-nums">{line.qty}</span>
                  )}
                </td>
                <td className="py-2 pr-4 text-right">
                  {editing ? (
                    <Input type="number" min="0" step="0.01" value={line.unit_price} onChange={(e) => handleLineChange(line.id, 'unit_price', e.target.value)} className="w-24 h-7 text-right text-sm" data-testid={`edit-price-${line.id}`} />
                  ) : (
                    <span className="tabular-nums">${line.unit_price.toFixed(2)}</span>
                  )}
                </td>
                <td className="py-2 text-right tabular-nums font-medium">${(line.qty * line.unit_price).toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="font-semibold">
              <td colSpan={3} className="pt-2 text-right">Subtotal:</td>
              <td className="pt-2 text-right tabular-nums" data-testid={`draft-subtotal-${draft.id}`}>${subtotal.toFixed(2)}</td>
            </tr>
          </tfoot>
        </table>

        {/* Notes */}
        {(editing || draft.notes) && (
          <div className="mt-2">
            {editing ? (
              <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes..." rows={2} className="text-sm" data-testid="draft-notes-input" />
            ) : draft.notes ? (
              <p className="text-xs text-muted-foreground italic mt-1">{draft.notes}</p>
            ) : null}
          </div>
        )}

        {/* Actions */}
        {canEdit && isDraft && (
          <div className="flex justify-end gap-2 mt-3">
            {editing ? (
              <>
                <Button variant="outline" size="sm" onClick={() => { setEditing(false); setLines(draft.lines || []); setNotes(draft.notes || ''); }}>
                  <X className="w-4 h-4 mr-1" /> Cancel
                </Button>
                <Button size="sm" onClick={handleSave} disabled={saving} data-testid={`save-draft-${draft.id}`}>
                  {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}
                  Save Changes
                </Button>
              </>
            ) : (
              <Button size="sm" onClick={handleFinalize} disabled={finalizing} data-testid={`finalize-draft-${draft.id}`}>
                {finalizing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-1" />}
                Finalize Invoice
              </Button>
            )}
          </div>
        )}
        {/* Post-finalization actions */}
        {draft.status === 'finalized' && canEdit && (
          <div className="flex justify-end gap-2 mt-3 pt-2 border-t">
            <Button variant="outline" size="sm" onClick={handleOpenInEditor} disabled={convertingToInvoice} data-testid={`open-editor-${draft.id}`}>
              {convertingToInvoice ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <ExternalLink className="w-3.5 h-3.5 mr-1" />}
              Open in Invoice Editor
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
